import { ShortLinkApi } from "@/apis/v2/ShortLinkApi";
import { CustomBackdropController } from "@/components/backdrop/default/v1";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const QUERY_KEY = 'ShortLinks';
const PAGE_SIZE = 50;

function handleError(error) {
    if (!error?.response) {
        customToast.error('서버와의 연결이 원활하지 않습니다.');
        return;
    }

    customToast.error(error.response?.data?.message);
}

// group: undefined 면 전체, null 이면 그룹 없는 것만, 문자열이면 그 그룹만
export function useShortLinksQuery({ group } = {}) {
    return useInfiniteQuery({
        queryKey: [QUERY_KEY, 'list', { group }],
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const params = { offset: pageParam, limit: PAGE_SIZE };
            if (group !== undefined) {
                params.group = group ?? '';
            }
            const response = await ShortLinkApi.getShortLinks({ params });

            return response?.data?.data;
        },
        getNextPageParam: (lastPage) => {
            const nextOffset = lastPage.offset + lastPage.items.length;
            return nextOffset < lastPage.total ? nextOffset : undefined;
        },
    });
}

// 통계는 보는 동안 새 클릭이 쌓이므로 열 때마다 다시 불러온다 (기본 staleTime: Infinity 무시)
export function useShortLinkStatsQuery({ id, from, to }) {
    return useQuery({
        queryKey: [QUERY_KEY, 'stats', id, from, to],
        enabled: !!id,
        staleTime: 0,
        queryFn: async () => {
            const response = await ShortLinkApi.getShortLinkStats({ id, params: { from, to } });

            return response?.data?.data;
        },
    });
}

// 목록만 다시 불러온다 (통계 쿼리까지 같이 무효화하지 않도록)
export function useRefreshShortLinkList() {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'list'] });
}

function useShortLinkMutation(mutationFn) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (variables) => {
            const response = await mutationFn(variables);

            return response?.data?.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: handleError,
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useCreateShortLinkMutation() {
    return useShortLinkMutation(({ body }) => ShortLinkApi.createShortLink({ body }));
}

export function useUpdateShortLinkMutation() {
    return useShortLinkMutation(({ id, body }) => ShortLinkApi.updateShortLink({ id, body }));
}

export function useDeleteShortLinkMutation() {
    return useShortLinkMutation(({ id }) => ShortLinkApi.deleteShortLink({ id }));
}

export function useRenameShortLinkGroupMutation() {
    return useShortLinkMutation(({ body }) => ShortLinkApi.renameShortLinkGroup({ body }));
}
